import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';

class BuildABoxMembershipComponent extends Component {
  static #count = 0;
  #id = ++BuildABoxMembershipComponent.#count;

  /** @type {{ label: string, description: string, monthlyVariantId: string, monthlySellingPlanId: string, annualVariantId: string, annualSellingPlanId: string, semiMonthlyVariantId: string, semiMonthlySellingPlanId: string }[]} */
  #tiers = [];
  /** @type {object | null} */ #boxData = null;
  /** @type {Map<string, number>} */ #selectedItems = new Map();
  /** @type {Map<string, string>} */ #variantTitles = new Map();
  /** @type {'slider' | 'grid'} */ #viewMode = 'slider';
  #activeTierIndex = 0;
  /** @type {'monthly' | 'annual' | 'semi-monthly'} */ #activeFrequency = 'monthly';
  /** @type {Map<string, object>} */ #boxCache = new Map();
  #scrollTrackReady = false;
  /** @type {Map<string, object>} */ #metafieldCache = new Map();

  connectedCallback() {
    super.connectedCallback();
    this.dataset.view = this.#viewMode;
    this.#readTiers();
    this.#renderTierSelector();
    this.#renderFrequencySelector();
    this.#renderProductGrid();
    this.#loadBox(true);
    this.#prefetchTierPrices();
  }

  async #prefetchTierPrices() {
    const fetches = this.#tiers.flatMap((tier, index) => {
      const tasks = [];
      // Monthly for non-active tiers (active tier's monthly is handled by #loadBox)
      if (index !== this.#activeTierIndex && tier.monthlyVariantId) {
        tasks.push(this.#fetchTierPrice(index, 'monthly', tier.monthlyVariantId, tier.monthlySellingPlanId));
      }
      // Annual for all tiers — needed so the tier-cards pricing toggle has values on page load
      if (tier.annualVariantId) {
        tasks.push(this.#fetchTierPrice(index, 'annual', tier.annualVariantId, tier.annualSellingPlanId));
      }
      // Semi-monthly for all tiers
      if (tier.semiMonthlyVariantId) {
        tasks.push(
          this.#fetchTierPrice(index, 'semi-monthly', tier.semiMonthlyVariantId, tier.semiMonthlySellingPlanId)
        );
      }
      return tasks;
    });
    await Promise.allSettled(fetches);
  }

  async #fetchTierPrice(index, frequency, variantId, sellingPlanId) {
    const cacheKey = `${index}:${frequency}`;
    try {
      const fetchBody = { productVariantPlatformId: variantId };
      if (sellingPlanId) fetchBody.sellingPlanPlatformId = sellingPlanId;
      const res = await fetch('https://api.skio.com/storefront-http/get-classic-box-v2', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fetchBody),
      });
      const data = await res.json();
      if (!data.ok) return;
      const boxData = data.ClassicBoxV2;
      this.#boxCache.set(cacheKey, boxData);
      this.dispatchEvent(
        new CustomEvent('bab:price-loaded', {
          bubbles: true,
          detail: {
            tierIndex: index,
            frequency,
            price: boxData.price,
            currency: boxData.currencyCode || this.dataset.currency || 'USD',
          },
        })
      );
    } catch {}
  }

  #readTiers() {
    this.#tiers = [...this.querySelectorAll('.bab-tier-data')].map((el) => ({
      label: el.dataset.label || '',
      description: el.dataset.description || '',
      monthlyVariantId: (el.dataset.monthlyVariantId ?? '').trim(),
      monthlySellingPlanId: (el.dataset.monthlySellingPlanId ?? '').trim(),
      annualVariantId: (el.dataset.annualVariantId ?? '').trim(),
      annualSellingPlanId: (el.dataset.annualSellingPlanId ?? '').trim(),
      semiMonthlyVariantId: (el.dataset.semiMonthlyVariantId ?? '').trim(),
      semiMonthlySellingPlanId: (el.dataset.semiMonthlySellingPlanId ?? '').trim(),
    }));
  }

  #tier() {
    return this.#tiers[this.#activeTierIndex] ?? {};
  }

  #variantId() {
    const t = this.#tier();
    if (this.#activeFrequency === 'annual') return t.annualVariantId ?? '';
    if (this.#activeFrequency === 'semi-monthly') return t.semiMonthlyVariantId ?? '';
    return t.monthlyVariantId ?? '';
  }

  #sellingPlanId() {
    const t = this.#tier();
    if (this.#activeFrequency === 'annual') return t.annualSellingPlanId ?? '';
    if (this.#activeFrequency === 'semi-monthly') return t.semiMonthlySellingPlanId ?? '';
    return t.monthlySellingPlanId ?? '';
  }

  #renderTierSelector() {
    const slot = this.querySelector('.bab-tier-selector-slot');
    if (!slot || this.#tiers.length <= 1) return;

    slot.innerHTML = `
      <div class="bab-tier-selector" role="group" aria-label="Membership tier">
        ${this.#tiers
          .map(
            (tier, i) => `
          <label class="bab-tier-swatch">
            <input type="radio" name="bab-tier-${this.#id}" value="${i}" on:change="/setTier" ${i === 0 ? 'checked' : ''}>
            <span class="bab-tier-swatch__label">${this.#escHtml(tier.label || `Tier ${i + 1}`)}</span>
            ${tier.description ? `<span class="bab-tier-swatch__desc">${this.#escHtml(tier.description)}</span>` : ''}
          </label>
        `
          )
          .join('')}
      </div>`;
  }

  #renderFrequencySelector() {
    const slot = this.querySelector('.bab-frequency-selector-slot');
    if (!slot) return;

    const monthlyLabel = this.#escHtml(slot.dataset.monthlyLabel || 'Monthly');
    const monthlyDesc = (slot.dataset.monthlyDescription || '').trim();
    const annualLabel = this.#escHtml(slot.dataset.annualLabel || 'Annual');
    const annualDesc = (slot.dataset.annualDescription || '').trim();
    const semiMonthlyLabel = (slot.dataset.semiMonthlyLabel || '').trim();
    const semiMonthlyDesc = (slot.dataset.semiMonthlyDescription || '').trim();

    const hasAnnual = this.#tiers.some((t) => t.annualVariantId);
    const hasSemiMonthly = !!semiMonthlyLabel;

    if (!hasAnnual && !hasSemiMonthly) {
      slot.hidden = true;
      return;
    }

    slot.innerHTML = `
      <div class="bab-purchase-options bab-purchase-options--frequency" role="group" aria-label="Billing frequency">
        <label class="bab-purchase-option" data-option="monthly">
          <input type="radio" name="bab-frequency-${this.#id}" value="monthly" on:change="/setFrequency" checked>
          <div class="bab-purchase-option__row">
            <span class="bab-purchase-option__title">${monthlyLabel}</span>
          </div>
          ${monthlyDesc ? `<span class="bab-tier-swatch__desc">${this.#escHtml(monthlyDesc)}</span>` : ''}
        </label>
        ${
          hasAnnual
            ? `
        <label class="bab-purchase-option" data-option="annual">
          <input type="radio" name="bab-frequency-${this.#id}" value="annual" on:change="/setFrequency">
          <div class="bab-purchase-option__row">
            <span class="bab-purchase-option__title">${annualLabel}</span>
          </div>
          ${annualDesc ? `<span class="bab-tier-swatch__desc">${this.#escHtml(annualDesc)}</span>` : ''}
        </label>`
            : ''
        }
        ${
          hasSemiMonthly
            ? `
        <label class="bab-purchase-option" data-option="semi-monthly">
          <input type="radio" name="bab-frequency-${this.#id}" value="semi-monthly" on:change="/setFrequency">
          <div class="bab-purchase-option__row">
            <span class="bab-purchase-option__title">${this.#escHtml(semiMonthlyLabel)}</span>
          </div>
          ${semiMonthlyDesc ? `<span class="bab-tier-swatch__desc">${this.#escHtml(semiMonthlyDesc)}</span>` : ''}
        </label>`
            : ''
        }
      </div>`;
  }

  #renderProductGrid() {
    const slot = this.querySelector('.bab-product-grid-slot');
    if (!slot) return;

    const customLabel = slot.dataset.buttonLabel?.trim();
    const atcLabel = customLabel || `Become a Rebel ${this.#escHtml(this.#tier().label || '')} Member`;

    slot.innerHTML = `
      <div class="bab-grid-header">
        <div class="bab-view-toggle" role="group" aria-label="View mode">
          <label class="bab-view-toggle__label">
            <input type="radio" name="bab-view-${this.#id}" value="slider" on:change="/setViewMode" ${this.#viewMode === 'slider' ? 'checked' : ''}>
            Slider
          </label>
          <label class="bab-view-toggle__label">
            <input type="radio" name="bab-view-${this.#id}" value="grid" on:change="/setViewMode" ${this.#viewMode === 'grid' ? 'checked' : ''}>
            Grid
          </label>
        </div>
        <div class="bab-scroll-track">
          <div class="bab-scroll-thumb" ref="scrollThumb"></div>
        </div>
      </div>

      <div class="bab-slider-wrap">
        <div class="bab-grid" aria-label="Available products">
          ${Array.from({ length: 4 }, () => '<div class="bab-skeleton-card" aria-hidden="true"></div>').join('')}
        </div>
        <div class="bab-slider-controls">
          <button class="bab-slider-btn bab-slider-btn--prev" type="button" on:click="/prevSlide" aria-label="Previous products">&#8249;</button>
          <button class="bab-slider-btn bab-slider-btn--next" type="button" on:click="/nextSlide" aria-label="Next products">&#8250;</button>
        </div>
      </div>

      <div class="bab-controls">
        <p class="bab-counter" ref="counter" role="status"></p>
        <p class="bab-error" ref="errorMessage" role="alert" hidden></p>
        <button
          class="bab-add-btn"
          ref="addToCartBtn"
          type="button"
          on:click="/handleAddToCart"
          disabled
          aria-disabled="true"
        >
          <span class="bab-add-btn__label">${atcLabel}</span>
          <span class="bab-add-btn__price" ref="btnPrice"></span>
        </button>
      </div>`;

    this.#scrollTrackReady = false;
  }

  async #loadBox(clearSelections) {
    const variantId = this.#variantId();
    if (!variantId) return;

    const cacheKey = `${this.#activeTierIndex}:${this.#activeFrequency}`;
    let boxData = this.#boxCache.get(cacheKey);

    if (!boxData) {
      const grid = this.querySelector('.bab-grid');
      if (grid) {
        grid.setAttribute('aria-busy', 'true');
        grid.innerHTML = Array.from(
          { length: 4 },
          () => '<div class="bab-skeleton-card" aria-hidden="true"></div>'
        ).join('');
      }

      try {
        const sellingPlanId = this.#sellingPlanId();
        const fetchBody = { productVariantPlatformId: variantId };
        if (sellingPlanId) fetchBody.sellingPlanPlatformId = sellingPlanId;

        const res = await fetch('https://api.skio.com/storefront-http/get-classic-box-v2', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(fetchBody),
        });
        const data = await res.json();
        if (!data.ok) throw new Error(`Failed to get box: ${JSON.stringify(data)}`);
        boxData = data.ClassicBoxV2;
        this.#boxCache.set(cacheKey, boxData);
      } catch (err) {
        console.error('[build-a-box-membership] fetch failed:', err);
        const grid = this.querySelector('.bab-grid');
        if (grid)
          grid.innerHTML = `<p class="bab-error" role="alert">Unable to load products. Please refresh the page.</p>`;
        return;
      }
    }

    this.#boxData = boxData;

    const descKey = (this.dataset.productDescriptionMetafield ?? '').trim();
    const allergensKey = (this.dataset.productAllergensMetafield ?? '').trim();
    if (descKey || allergensKey) {
      const slugs = [
        ...new Set((boxData.selectableProductVariants ?? []).map((v) => v.productSlug ?? '').filter(Boolean)),
      ];
      const keys = [descKey, allergensKey].filter(Boolean);
      await this.#fetchAllMetafields(slugs, keys);
    }

    this.#renderGrid(clearSelections);
  }

  #renderGrid(clearSelections) {
    const box = this.#boxData;
    const grid = this.querySelector('.bab-grid');
    if (!box || !grid) return;

    const validIds = new Set((box.selectableProductVariants ?? []).map((v) => String(v.platformId).split('/').pop()));

    if (clearSelections) {
      this.#selectedItems.clear();
    } else {
      for (const id of this.#selectedItems.keys()) {
        if (!validIds.has(id)) this.#selectedItems.delete(id);
      }
    }

    const staticDescription = this.dataset.productDescription ?? '';
    const staticAllergens = this.dataset.productAllergens ?? '';
    const descKey = (this.dataset.productDescriptionMetafield ?? '').trim();
    const allergensKey = (this.dataset.productAllergensMetafield ?? '').trim();

    grid.removeAttribute('aria-busy');
    grid.innerHTML = (box.selectableProductVariants ?? [])
      .map((variant) => {
        const numericId = String(variant.platformId).split('/').pop() ?? '';
        const productTitle = variant.productTitle ?? '';
        this.#variantTitles.set(numericId, productTitle);
        const titleEscaped = this.#escHtml(productTitle);
        const variantTitle = variant.productVariantTitle ?? '';
        const imageSrc = variant.imageSrc || '';

        const metafields = this.#metafieldCache.get(variant.productSlug ?? '') ?? {};
        const description =
          (descKey ? BuildABoxMembershipComponent.#metafieldValue(metafields, descKey) : '') || staticDescription;
        const allergens =
          (allergensKey ? BuildABoxMembershipComponent.#metafieldValue(metafields, allergensKey) : '') ||
          staticAllergens;

        return `
          <div class="bab-card" data-variant-id="${numericId}" data-selected="false" on:click="/cardClick">
            <div class="bab-card__img-wrap">
              ${
                imageSrc
                  ? `<img src="${imageSrc}" alt="${titleEscaped}" loading="lazy" width="400" height="400">`
                  : `<div class="bab-card__img-placeholder" aria-hidden="true"></div>`
              }
              <span class="bab-card__checkbox" aria-hidden="true"></span>
            </div>
            <div class="bab-card__body">
              <span class="bab-card__title">${titleEscaped}</span>
              ${
                variantTitle && variantTitle !== 'Default Title'
                  ? `<span class="bab-card__variant">${this.#escHtml(variantTitle)}</span>`
                  : ''
              }
              ${description ? `<p class="bab-card__description">${this.#escHtml(description)}</p>` : ''}
              ${allergens ? `<p class="bab-card__allergens">${this.#escHtml(allergens)}</p>` : ''}
              <div class="bab-card__qty-row">
                <button class="bab-card__qty-btn" type="button" data-variant-id="${numericId}" data-action="dec" on:click="/decrementCard" aria-label="Remove ${titleEscaped}" disabled>−</button>
                <span class="bab-card__qty-value" aria-live="polite">0</span>
                <button class="bab-card__qty-btn bab-card__qty-btn--inc" type="button" data-variant-id="${numericId}" data-action="inc" on:click="/incrementCard" aria-label="Add ${titleEscaped}">+</button>
              </div>
            </div>
          </div>`;
      })
      .join('');

    for (const [variantId] of this.#selectedItems) {
      this.#updateCard(variantId);
    }

    const btnPrice = this.querySelector('[ref="btnPrice"]');
    const currency = box.currencyCode || this.dataset.currency || 'USD';
    if (btnPrice) {
      btnPrice.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
      }).format(box.price);
    }

    this.dispatchEvent(
      new CustomEvent('bab:price-loaded', {
        bubbles: true,
        detail: { tierIndex: this.#activeTierIndex, frequency: this.#activeFrequency, price: box.price, currency },
      })
    );

    this.#updateSummary();
    this.#setupScrollTrack();
  }

  /** @param {Event} event */
  setTier(event) {
    this.#activeTierIndex = Number(/** @type {HTMLInputElement} */ (event.target).value);
    const slot = this.querySelector('.bab-product-grid-slot');
    const customLabel = slot?.dataset.buttonLabel?.trim();
    if (!customLabel) {
      const labelEl = this.querySelector('.bab-add-btn__label');
      if (labelEl) labelEl.textContent = `Become a Rebel ${this.#tier().label || ''} Member`;
    }
    this.#loadBox(true);
  }

  /** @param {Event} event */
  setFrequency(event) {
    this.#activeFrequency = /** @type {'monthly' | 'annual' | 'semi-monthly'} */ (
      /** @type {HTMLInputElement} */ (event.target).value
    );
    this.#loadBox(false);
  }

  /** @param {Event} event */
  setViewMode(event) {
    this.#viewMode = /** @type {'slider' | 'grid'} */ (/** @type {HTMLInputElement} */ (event.target).value);
    this.dataset.view = this.#viewMode;
    if (this.#viewMode === 'slider') this.#updateScrollThumb();
  }

  #setupScrollTrack() {
    const grid = this.querySelector('.bab-grid');
    if (!grid) return;
    if (!this.#scrollTrackReady) {
      this.#scrollTrackReady = true;
      grid.addEventListener('scroll', () => this.#updateScrollThumb(), { passive: true });
    }
    this.#updateScrollThumb();
  }

  #updateScrollThumb() {
    const grid = this.querySelector('.bab-grid');
    const thumb = /** @type {HTMLElement | null} */ (this.querySelector('[ref="scrollThumb"]'));
    if (!grid || !thumb) return;
    const { scrollLeft, scrollWidth, clientWidth } = grid;
    const thumbPct = (clientWidth / scrollWidth) * 100;
    const leftPct = scrollWidth > clientWidth ? (scrollLeft / (scrollWidth - clientWidth)) * (100 - thumbPct) : 0;
    thumb.style.width = `${thumbPct}%`;
    thumb.style.left = `${leftPct}%`;
  }

  prevSlide() {
    const grid = this.querySelector('.bab-grid');
    if (!grid) return;
    grid.scrollBy({ left: -grid.clientWidth, behavior: 'smooth' });
  }

  nextSlide() {
    const grid = this.querySelector('.bab-grid');
    if (!grid) return;
    grid.scrollBy({ left: grid.clientWidth, behavior: 'smooth' });
  }

  /** @param {MouseEvent} event */
  cardClick(event) {
    if (/** @type {HTMLElement} */ (event.target).closest('.bab-card__qty-btn')) return;
    const card = /** @type {HTMLElement | null} */ (/** @type {HTMLElement} */ (event.target).closest('.bab-card'));
    if (!card) return;

    const variantId = card.dataset.variantId ?? '';
    if (!variantId) return;

    if (card.dataset.selected === 'true') {
      this.#selectedItems.delete(variantId);
      this.#updateCard(variantId);
      this.#updateSummary();
      return;
    }

    const size = this.#boxData?.size ?? 0;
    const total = [...this.#selectedItems.values()].reduce((s, q) => s + q, 0);
    if (total >= size) return;

    this.#selectedItems.set(variantId, 1);
    this.#updateCard(variantId);
    this.#updateSummary();
  }

  /** @param {MouseEvent} event */
  incrementCard(event) {
    const btn = /** @type {HTMLElement} */ (event.target).closest('[data-action="inc"]');
    const variantId = /** @type {HTMLElement | null} */ (btn)?.dataset.variantId ?? '';
    if (!variantId) return;

    const size = this.#boxData?.size ?? 0;
    const total = [...this.#selectedItems.values()].reduce((s, q) => s + q, 0);
    if (total >= size) return;

    this.#selectedItems.set(variantId, (this.#selectedItems.get(variantId) ?? 0) + 1);
    this.#updateCard(variantId);
    this.#updateSummary();
  }

  /** @param {MouseEvent} event */
  decrementCard(event) {
    const btn = /** @type {HTMLElement} */ (event.target).closest('[data-action="dec"]');
    const variantId = /** @type {HTMLElement | null} */ (btn)?.dataset.variantId ?? '';
    if (!variantId) return;

    const qty = this.#selectedItems.get(variantId) ?? 0;
    if (qty <= 1) {
      this.#selectedItems.delete(variantId);
    } else {
      this.#selectedItems.set(variantId, qty - 1);
    }
    this.#updateCard(variantId);
    this.#updateSummary();
  }

  /** @param {string} variantId */
  #updateCard(variantId) {
    const card = this.querySelector(`.bab-card[data-variant-id="${variantId}"]`);
    if (!card) return;

    const qty = this.#selectedItems.get(variantId) ?? 0;
    card.setAttribute('data-selected', String(qty > 0));

    const qtyValue = card.querySelector('.bab-card__qty-value');
    if (qtyValue) qtyValue.textContent = String(qty);

    const decBtn = card.querySelector('[data-action="dec"]');
    if (decBtn) decBtn.toggleAttribute('disabled', qty <= 0);
  }

  #updateSummary() {
    const size = this.#boxData?.size ?? 0;
    const total = [...this.#selectedItems.values()].reduce((s, q) => s + q, 0);

    const counter = this.querySelector('[ref="counter"]');
    if (counter) counter.textContent = `${total} of ${size} selected`;

    const btn = /** @type {HTMLButtonElement | null} */ (this.querySelector('[ref="addToCartBtn"]'));
    if (btn) {
      btn.disabled = total < size;
      btn.setAttribute('aria-disabled', String(total < size));
    }

    this.querySelectorAll('.bab-card__qty-btn--inc').forEach((incBtn) =>
      incBtn.toggleAttribute('disabled', total >= size)
    );
  }

  #buildBoxItems() {
    const box = this.#boxData;
    if (!box) return null;

    const total = [...this.#selectedItems.values()].reduce((s, q) => s + q, 0);
    if (total < box.size) return null;

    const numericVariantId = Number(this.#variantId());
    const sellingPlanGid = box.sellingPlan;
    const sellingPlanFromResponse = sellingPlanGid ? Number(String(sellingPlanGid).split('/').pop()) : null;
    const sellingPlanFromSettings = Number(this.#sellingPlanId()) || null;
    const sellingPlanId = sellingPlanFromResponse || sellingPlanFromSettings;

    const properties = {};
    for (const [numericId, qty] of this.#selectedItems) {
      if (qty > 0) {
        properties[`_pvgid://shopify/ProductVariant/${numericId}`] = String(qty);
        const title = this.#variantTitles.get(numericId);
        if (title) properties[title] = String(qty);
      }
    }

    return [
      {
        id: numericVariantId,
        quantity: 1,
        ...(sellingPlanId ? { selling_plan: sellingPlanId } : {}),
        properties,
      },
    ];
  }

  async #submitToCart(items) {
    const btn = /** @type {HTMLButtonElement | null} */ (this.querySelector('[ref="addToCartBtn"]'));
    if (btn) btn.setAttribute('aria-busy', 'true');

    try {
      const res = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', {
          body: JSON.stringify({ items }),
        })
      );
      const data = await res.json();

      if (data.status) {
        this.#showError(data.message ?? 'Unable to add to cart.');
        return;
      }

      document.dispatchEvent(
        new CartAddEvent(data, String(items[0].id), {
          source: 'build-a-box-membership-component',
          itemCount: 1,
        })
      );
    } catch {
      this.#showError('Something went wrong. Please try again.');
    } finally {
      if (btn) btn.removeAttribute('aria-busy');
    }
  }

  async handleAddToCart() {
    const items = this.#buildBoxItems();
    if (!items) return;
    await this.#submitToCart(items);
  }

  /** @param {string} message */
  #showError(message) {
    const el = this.querySelector('[ref="errorMessage"]');
    if (!el) return;
    el.textContent = message;
    el.removeAttribute('hidden');
    setTimeout(() => el?.setAttribute('hidden', ''), 8000);
  }

  /**
   * @param {object} data — flat map of "namespace.key" → value strings
   * @param {string} namespaceKey — e.g. "custom.short_description"
   */
  static #metafieldValue(data, namespaceKey) {
    if (!namespaceKey || !data) return '';
    return String(data[namespaceKey] ?? '');
  }

  /**
   * Fetches metafields for all slugs in a single batched Storefront API request.
   * Results are stored in #metafieldCache as flat { "namespace.key": "value" } maps.
   * @param {string[]} slugs
   * @param {string[]} keys — namespace.key strings, e.g. ["custom.short_description"]
   */
  async #fetchAllMetafields(slugs, keys) {
    const shopDomain = (this.dataset.shopDomain ?? '').trim();
    const token = (this.dataset.storefrontToken ?? '').trim();
    if (!shopDomain || !token || !slugs.length || !keys.length) return;

    const uncached = slugs.filter((s) => !this.#metafieldCache.has(s));
    if (!uncached.length) return;

    const identifiers = keys.map((k) => {
      const dot = k.indexOf('.');
      return { namespace: k.slice(0, dot), key: k.slice(dot + 1) };
    });
    const identifiersGql = identifiers
      .map((id) => `{namespace: ${JSON.stringify(id.namespace)}, key: ${JSON.stringify(id.key)}}`)
      .join(', ');
    const queryBody = uncached
      .map(
        (slug, i) =>
          `p${i}: product(handle: ${JSON.stringify(slug)}) { metafields(identifiers: [${identifiersGql}]) { namespace key value } }`
      )
      .join(' ');

    try {
      const res = await fetch(`https://${shopDomain}/api/2026-04/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({ query: `{ ${queryBody} }` }),
      });
      const { data } = await res.json();
      uncached.forEach((slug, i) => {
        const flat = {};
        for (const mf of data?.[`p${i}`]?.metafields ?? []) {
          if (mf) flat[`${mf.namespace}.${mf.key}`] = mf.value;
        }
        this.#metafieldCache.set(slug, flat);
      });
    } catch {
      uncached.forEach((slug) => this.#metafieldCache.set(slug, {}));
    }
  }

  /** @param {string} str */
  #escHtml(str) {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
}

if (!customElements.get('build-a-box-membership-component')) {
  customElements.define('build-a-box-membership-component', BuildABoxMembershipComponent);
}
